import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
// import { api, ApiClientError } from "@/lib/api"; // Uncomment when backend is ready

// Define the DNS type
interface DNS {
  id: string;
  dns_name: string;
  ncii_instances: number;
  type: string;
  region: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  contacts: string[]; // Array of email addresses
}

const PAGE_SIZE = 50;

export function DNSList() {
  const { logout } = useAuth();
  const [dnsProviders, setDnsProviders] = useState<DNS[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadDNSProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call
      // const data = await api.dns.list(PAGE_SIZE, currentPage);
      // setDnsProviders(data);
      // setHasMore(data.length === PAGE_SIZE);
      // setTotalCount(data.totalCount); // Assuming API returns total count

      // Mock data for now
      const mockDNSProviders: DNS[] = [
        {
          id: "1",
          dns_name: "Cloudflare DNS",
          ncii_instances: 1456,
          type: "Public DNS",
          region: "Global",
          active_urls: 271,
          removed_urls: 1185,
          takedown_rate: 0.81,
          avg_response_time: 2.0,
          contacts: ["abuse@cloudflare.com", "dns-abuse@cloudflare.com"],
        },
        {
          id: "2",
          dns_name: "Google Public DNS",
          ncii_instances: 1234,
          type: "Public DNS",
          region: "Global",
          active_urls: 230,
          removed_urls: 1004,
          takedown_rate: 0.81,
          avg_response_time: 1.8,
          contacts: ["abuse@google.com"],
        },
        {
          id: "3",
          dns_name: "Quad9",
          ncii_instances: 892,
          type: "Security DNS",
          region: "Global",
          active_urls: 166,
          removed_urls: 726,
          takedown_rate: 0.81,
          avg_response_time: 1.5,
          contacts: ["abuse@quad9.net", "security@quad9.net"],
        },
        {
          id: "4",
          dns_name: "OpenDNS (Cisco)",
          ncii_instances: 1067,
          type: "Security DNS",
          region: "Global",
          active_urls: 199,
          removed_urls: 868,
          takedown_rate: 0.81,
          avg_response_time: 2.2,
          contacts: ["abuse@opendns.com"],
        },
        {
          id: "5",
          dns_name: "Amazon Route 53",
          ncii_instances: 734,
          type: "Managed DNS",
          region: "Global",
          active_urls: 137,
          removed_urls: 597,
          takedown_rate: 0.81,
          avg_response_time: 2.1,
          contacts: ["abuse@amazonaws.com", "route53-abuse@amazon.com"],
        },
        {
          id: "6",
          dns_name: "Verisign Public DNS",
          ncii_instances: 456,
          type: "Public DNS",
          region: "Global",
          active_urls: 85,
          removed_urls: 371,
          takedown_rate: 0.81,
          avg_response_time: 2.3,
          contacts: ["abuse@verisign.com"],
        },
        {
          id: "7",
          dns_name: "AdGuard DNS",
          ncii_instances: 389,
          type: "Privacy DNS",
          region: "Global",
          active_urls: 72,
          removed_urls: 317,
          takedown_rate: 0.81,
          avg_response_time: 1.9,
          contacts: ["abuse@adguard.com", "dns@adguard.com"],
        },
        {
          id: "8",
          dns_name: "CleanBrowsing",
          ncii_instances: 298,
          type: "Family DNS",
          region: "Global",
          active_urls: 56,
          removed_urls: 242,
          takedown_rate: 0.81,
          avg_response_time: 1.7,
          contacts: ["abuse@cleanbrowsing.org"],
        },
        {
          id: "9",
          dns_name: "NextDNS",
          ncii_instances: 523,
          type: "Privacy DNS",
          region: "Global",
          active_urls: 97,
          removed_urls: 426,
          takedown_rate: 0.81,
          avg_response_time: 1.6,
          contacts: ["abuse@nextdns.io", "security@nextdns.io"],
        },
        {
          id: "10",
          dns_name: "Control D",
          ncii_instances: 234,
          type: "Privacy DNS",
          region: "Global",
          active_urls: 44,
          removed_urls: 190,
          takedown_rate: 0.81,
          avg_response_time: 1.8,
          contacts: ["abuse@controld.com"],
        },
      ];
      setDnsProviders(mockDNSProviders);
      setHasMore(false);
      setTotalCount(mockDNSProviders.length); // For mock data, total equals current data length
    } catch (err) {
      // if (err instanceof ApiClientError) {
      //   if (err.status === 401) {
      //     logout();
      //   } else {
      //     setError(err.detail || "Failed to load DNS providers");
      //   }
      // } else {
      //   setError("Failed to load DNS providers");
      // }
      setError("Failed to load DNS providers");
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadDNSProviders();
  }, [loadDNSProviders]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the DNS Providers table
  const columns: ColumnDef<DNS>[] = [
    {
      key: "dns_name",
      header: "DNS Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.dns_name}
        </div>
      ),
      sortValue: (item) => item.dns_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region}</div>
      ),
      sortValue: (item) => item.region,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts.join(", ")}
        </div>
      ),
      sortValue: (item) => item.contacts.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to DNS details or open modal
            console.log("View DNS provider:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];

  // Handle new DNS provider creation
  const handleNewDNS = () => {
    // TODO: Implement new DNS provider modal or navigation
    console.log("Add new DNS provider");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export DNS providers");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewDNS}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add DNS Provider
      </Button>
    </>
  );

  return (
    <DataTable
      title="DNS Providers"
      subtitle="Track DNS providers and blocking capabilities"
      columns={columns}
      data={dnsProviders}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search DNS providers..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="DNS providers"
    />
  );
}



